import React, { useState, useMemo } from 'react';
import { BookOpen, User, CheckCircle, AlertCircle, Sparkles, Copy, RefreshCw, ChevronDown, Search, FileText } from 'lucide-react';
import STUDENTS_DATA from './data/studentsData.json';
import { fetchWithGeminiResilience } from './geminiClient';

// --- DADOS FORNECIDOS (BANCO DE DADOS ATUALIZADO) ---
const OBSERVATION_POINTS = [
    // --- POSITIVOS ---
    { id: 'P01', type: 'Positivo', category: 'Engajamento', description: 'Demonstra interesse pelas atividades propostas.' },
    { id: 'P02', type: 'Positivo', category: 'Participação', description: 'Participa ativamente das aulas e discussões.' },
    { id: 'P03', type: 'Positivo', category: 'Atenção', description: 'Apresenta bom nível de concentração durante as explicações.' },
    { id: 'P04', type: 'Positivo', category: 'Responsabilidade', description: 'Realiza as tarefas com responsabilidade e dentro dos prazos.' },
    { id: 'P05', type: 'Positivo', category: 'Autonomia', description: 'Mostra autonomia na organização dos estudos.' },
    { id: 'P06', type: 'Positivo', category: 'Convivência', description: 'Mantém postura respeitosa com colegas e professores.' },
    { id: 'P07', type: 'Positivo', category: 'Curiosidade', description: 'Demonstra curiosidade intelectual e vontade de aprender.' },
    { id: 'P08', type: 'Positivo', category: 'Evolução', description: 'Apresenta evolução consistente ao longo do período.' },
    { id: 'P09', type: 'Positivo', category: 'Compreensão', description: 'Possui boa capacidade de interpretação e compreensão dos conteúdos.' },
    { id: 'P10', type: 'Positivo', category: 'Comunicação', description: 'Expressa suas ideias com clareza, oralmente e/ou por escrito.' },
    { id: 'P11', type: 'Positivo', category: 'Colaboração', description: 'Trabalha bem em grupo, colaborando com os colegas.' },
    { id: 'P12', type: 'Positivo', category: 'Feedback', description: 'Aceita orientações e feedbacks de forma positiva.' },
    { id: 'P13', type: 'Positivo', category: 'Persistência', description: 'Demonstra perseverança diante de dificuldades.' },
    { id: 'P14', type: 'Positivo', category: 'Pontualidade', description: 'Mantém frequência regular e pontualidade satisfatória.' },
    { id: 'P15', type: 'Positivo', category: 'Domínio', description: 'Apresenta bom domínio dos conteúdos trabalhados.' },
    { id: 'P16', type: 'Positivo', category: 'Organização', description: 'Demonstra senso de responsabilidade com materiais e atividades.' },
    { id: 'P17', type: 'Positivo', category: 'Iniciativa', description: 'Mostra iniciativa na realização de tarefas e projetos.' },
    { id: 'P18', type: 'Positivo', category: 'Ética', description: 'Revela postura ética nas avaliações e atividades em sala.' },
    { id: 'P19', type: 'Positivo', category: 'Aplicação', description: 'Consegue aplicar os conhecimentos em diferentes situações.' },
    { id: 'P20', type: 'Positivo', category: 'Comportamento', description: 'Apresenta comportamento adequado ao ambiente escolar.' },

    // --- A DESENVOLVER ---
    { id: 'N01', type: 'A_Desenvolver', category: 'Atenção', description: 'Apresenta dificuldade em manter a concentração durante as aulas.' },
    { id: 'N02', type: 'A_Desenvolver', category: 'Engajamento', description: 'Demonstra pouco envolvimento nas atividades propostas.' },
    { id: 'N03', type: 'A_Desenvolver', category: 'Responsabilidade', description: 'Entrega tarefas com atraso ou deixa de realizá-las com frequência.' },
    { id: 'N04', type: 'A_Desenvolver', category: 'Organização', description: 'Necessita melhorar a organização dos materiais e dos estudos.' },
    { id: 'N05', type: 'A_Desenvolver', category: 'Colaboração', description: 'Mostra resistência em participar de atividades em grupo.' },
    { id: 'N06', type: 'A_Desenvolver', category: 'Compreensão', description: 'Apresenta dificuldade na compreensão de alguns conteúdos fundamentais.' },
    { id: 'N07', type: 'A_Desenvolver', category: 'Autonomia', description: 'Precisa desenvolver maior autonomia na realização das atividades.' },
    { id: 'N08', type: 'A_Desenvolver', category: 'Comunicação', description: 'Demonstra insegurança ao expor suas ideias.' },
    { id: 'N09', type: 'A_Desenvolver', category: 'Atenção', description: 'Apresenta postura dispersa em alguns momentos da aula.' },
    { id: 'N10', type: 'A_Desenvolver', category: 'Responsabilidade', description: 'Necessita aprimorar a responsabilidade com prazos e compromissos.' },
    { id: 'N11', type: 'A_Desenvolver', category: 'Iniciativa', description: 'Mostra pouca iniciativa diante das propostas pedagógicas.' },
    { id: 'N12', type: 'A_Desenvolver', category: 'Interpretação', description: 'Apresenta dificuldades na interpretação de textos e enunciados.' },
    { id: 'N13', type: 'A_Desenvolver', category: 'Constância', description: 'Precisa desenvolver maior constância nos estudos.' },
    { id: 'N14', type: 'A_Desenvolver', category: 'Feedback', description: 'Demonstra resistência em aceitar orientações e correções.' },
    { id: 'N15', type: 'A_Desenvolver', category: 'Convivência', description: 'Apresenta comportamento inadequado em alguns momentos do convívio escolar.' },
    { id: 'N16', type: 'A_Desenvolver', category: 'Produção', description: 'Necessita melhorar a qualidade e o capricho nas produções escritas.' },
    { id: 'N17', type: 'A_Desenvolver', category: 'Colaboração', description: 'Mostra dificuldade em trabalhar de forma colaborativa.' },
    { id: 'N18', type: 'A_Desenvolver', category: 'Avaliação', description: 'Apresenta baixo rendimento em avaliações formais.' },
    { id: 'N19', type: 'A_Desenvolver', category: 'Atenção', description: 'Precisa desenvolver maior atenção às explicações em sala.' },
    { id: 'N20', type: 'A_Desenvolver', category: 'Persistência', description: 'Demonstra pouca persistência diante de desafios acadêmicos.' },
];



// --- APENAS TRIMESTRES ---
const PERIODS = ['1º Trimestre', '2º Trimestre', '3º Trimestre'];

export default function StudentReportGenerator({ apiKey }) {
    // Estado para Inputs Básicos
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedStudent, setSelectedStudent] = useState('');
    const [tutorName, setTutorName] = useState('');
    const [period, setPeriod] = useState('');
    const [studentProfile, setStudentProfile] = useState('');
    const [highlightPoints, setHighlightPoints] = useState('');

    // Estado para Seleção de Pontos
    const [selectedPointIds, setSelectedPointIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado para IA
    const [generatedReport, setGeneratedReport] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Use the system apiKey if available, or fallback to hardcoded
    const effectiveApiKey = apiKey || "AQ.Ab8RN6L9xqG2T6VdoL7ChC1S_aV8DS1a1UWdXhScIx9CHlwsHQ";

    // --- LÓGICA DE SELEÇÃO ---

    const togglePoint = (id) => {
        setSelectedPointIds(prev =>
            prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
        );
    };

    const getSelectedPointsData = () => {
        return OBSERVATION_POINTS.filter(p => selectedPointIds.includes(p.id));
    };

    const filteredPoints = useMemo(() => {
        if (!searchTerm) return OBSERVATION_POINTS;
        const lower = searchTerm.toLowerCase();
        return OBSERVATION_POINTS.filter(p =>
            p.description.toLowerCase().includes(lower) ||
            p.category.toLowerCase().includes(lower)
        );
    }, [searchTerm]);

    const positivePoints = filteredPoints.filter(p => p.type === 'Positivo');
    const developPoints = filteredPoints.filter(p => p.type === 'A_Desenvolver');

    // --- CHAMADA À API GEMINI ---

    const generateReport = async () => {
        if (!selectedClass || !selectedStudent || !tutorName || !period) {
            setError('Por favor, preencha todos os campos obrigatórios (Turma, Aluno, Tutor, Período).');
            return;
        }

        if (!effectiveApiKey) {
            setError('Chave de API do Gemini não configurada. Contate o administrador.');
            return;
        }

        const selectedData = getSelectedPointsData();
        const positives = selectedData.filter(p => p.type === 'Positivo');
        const toDevelop = selectedData.filter(p => p.type === 'A_Desenvolver');

        if (positives.length === 0 && toDevelop.length === 0) {
            setError('Selecione pelo menos um ponto de observação.');
            return;
        }

        setIsLoading(true);
        setError('');
        setGeneratedReport('');

        // Extrair primeiro nome
        const studentFirstName = selectedStudent.split(' ')[0];

        // Construção do Prompt (Fiel ao pedido)
        const positiveDescriptions = positives.map(p => `- ${p.description}`).join('\n');
        const developDescriptions = toDevelop.map(p => `- ${p.description}`).join('\n');

        const prompt = `
      Você é o Tutor(a) educacional deste aluno na unidade Eleva Barra. 
      Sua missão é escrever um relato sobre o desenvolvimento do estudante neste período.
      
      IMPORTANTE: O texto NÃO deve parecer um relatório técnico frio. Deve ter um OLHAR HUMANO, afetuoso e individualizado. 
      Escreva como se estivesse conversando com a família sobre a jornada, os esforços e o crescimento do aluno, demonstrando que você realmente o observa e se importa.

      Informações do estudante:
      – Nome Completo: ${selectedStudent}
      – Nome para tratamento (use este no texto): ${studentFirstName}
      – Série: ${selectedClass}
      – Período avaliado: ${period}
      – Perfil geral (contexto extra): ${studentProfile || 'Não especificado'}
      – Pontos Específicos a Destacar (Prioridade Alta): ${highlightPoints || 'Nenhum ponto específico adicional.'}

      Observações Positivas (para integrar na narrativa):
      ${positiveDescriptions}

      Pontos de Atenção/Desenvolvimento (para abordar com cuidado e construtividade):
      ${developDescriptions}

      Diretrizes de Escrita:
      1. Comece citando o nome do aluno (${studentFirstName}) e criando uma conexão inicial positiva. Use Apenas o PRIMEIRO NOME dele ao longo do texto.
      2. Se houver "Pontos Específicos a Destacar", certifique-se de que eles guiem a narrativa ou ganhem destaque no texto.
      3. Integre os pontos positivos e os pontos a desenvolver em uma narrativa fluida. Evite frases soltas como "O aluno demonstra X. O aluno precisa Y.". Use conectivos que mostrem a relação entre as características.
      4. Ao falar dos desafios (pontos a desenvolver), use uma linguagem de "oportunidade de crescimento" e parceria, não de crítica pura. Mostre que a escola está ao lado dele.
      5. O tom deve ser de parceria, encorajamento e reconhecimento do esforço individual.
      6. Tamanho: Um parágrafo robusto e bem encadeado (entre 6 a 10 linhas).
      7. Finalize com uma mensagem breve de incentivo ou próximo passo pedagógico.
      8. Após o texto, pule uma linha e adicione a assinatura: "Tutor(a): ${tutorName}"
    `;

        try {
            const payload = {
                contents: [{ parts: [{ text: prompt }] }],
            };
            const response = await fetchWithGeminiResilience(effectiveApiKey, payload);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error?.message || `Erro ${response.status}: ${response.statusText}`;
                throw new Error(errorMessage);
            }

            const data = await response.json();
            const text = data.candidates?.[0]?.content?.parts?.[0]?.text || "Erro ao gerar texto.";
            setGeneratedReport(text);

        } catch (err) {
            console.error(err);
            setError(`Erro: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // --- COMPONENTES AUXILIARES ---

    const PointCard = ({ point, isSelected, onClick }) => (
        <div
            onClick={() => onClick(point.id)}
            className={`
        relative p-3 rounded-lg border cursor-pointer transition-all duration-200 text-sm mb-2 group
        ${isSelected
                    ? (point.type === 'Positivo' ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-yellow-50 border-yellow-500 shadow-sm')
                    : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }
      `}
        >
            <div className="flex justify-between items-start mb-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${point.type === 'Positivo' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {point.category}
                </span>
                {isSelected && <CheckCircle size={16} className={point.type === 'Positivo' ? 'text-green-600' : 'text-yellow-600'} />}
            </div>
            <p className="text-gray-800 font-medium mb-1">{point.description}</p>
            {point.intervention && (
                <p className="text-xs text-gray-500 italic truncate group-hover:whitespace-normal group-hover:bg-white group-hover:absolute group-hover:z-10 group-hover:p-2 group-hover:border group-hover:shadow-lg group-hover:rounded group-hover:w-full group-hover:left-0">
                    Sugestão: {point.intervention}
                </p>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-stone-800 p-4 md:p-8">
            <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                {/* CABEÇALHO COM LOGO */}
                <div className="lg:col-span-12 mb-2 flex flex-col md:flex-row items-center justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-200 gap-4">
                    <div className="flex items-center gap-4">
                        {/* LOGO ELEVA */}
                        <div className="h-16 w-auto flex items-center">
                            <img
                                src="https://i.postimg.cc/Vs77fcRR/Logo_Escola_Eleva_Barra_Horizontal_Inspired.png"
                                alt="Logo Escola Eleva"
                                className="h-full w-auto object-contain"
                            />
                        </div>
                        <div className="h-10 w-px bg-gray-300 hidden md:block"></div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
                                    Relatório do Tutor
                                </h1>
                            </div>
                            <p className="text-gray-500 text-sm mt-1">
                                Plataforma de Relatórios - <strong>Eleva Barra</strong>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                            <div className="text-xs text-blue-500 font-bold font-mono bg-blue-50 px-3 py-1 rounded-full">v1.9.1</div>
                        </div>
                    </div>
                </div>

                {/* COLUNA DA ESQUERDA: CONFIGURAÇÃO (STICKY) */}
                <div className="lg:col-span-3 space-y-4 lg:sticky lg:top-4 self-start">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <User size={16} className="text-blue-500" /> Dados do Aluno
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Nome do Tutor</label>
                                <input
                                    type="text"
                                    value={tutorName}
                                    onChange={(e) => setTutorName(e.target.value)}
                                    placeholder="Nome para assinatura"
                                    className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Turma</label>
                                <div className="relative">
                                    <select
                                        value={selectedClass}
                                        onChange={(e) => { setSelectedClass(e.target.value); setSelectedStudent(''); }}
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg appearance-none text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">Selecione a Turma</option>
                                        {Object.keys(STUDENTS_DATA).map(cls => (
                                            <option key={cls} value={cls}>{cls}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Aluno(a)</label>
                                <div className="relative">
                                    <select
                                        value={selectedStudent}
                                        onChange={(e) => setSelectedStudent(e.target.value)}
                                        disabled={!selectedClass}
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg appearance-none text-sm focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">Selecione o Aluno</option>
                                        {selectedClass && STUDENTS_DATA[selectedClass] && STUDENTS_DATA[selectedClass].map(std => (
                                            <option key={std} value={std}>{std}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Período</label>
                                <select
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value)}
                                    className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Selecione</option>
                                    {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Perfil Geral (Opcional)</label>
                                <textarea
                                    value={studentProfile}
                                    onChange={(e) => setStudentProfile(e.target.value)}
                                    placeholder="Ex: Aluno quieto, esforçado..."
                                    className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none h-20 resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Pontos que merecem ser destacados (Opcional)</label>
                                <textarea
                                    value={highlightPoints}
                                    onChange={(e) => setHighlightPoints(e.target.value)}
                                    placeholder="Ex: Melhorou muito em matemática, liderou o grupo de ciências..."
                                    className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none h-20 resize-none"
                                />
                            </div>
                        </div>
                    </div>

                </div>

                {/* COLUNA DO MEIO: SELETOR DE PONTOS */}
                <div className="lg:col-span-6 flex flex-col">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
                        {/* Barra de Busca STICKY */}
                        <div className="p-4 border-b border-gray-100 bg-gray-50 sticky top-0 z-30 rounded-t-xl">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar pontos (ex: foco, oralidade, respeito)..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                                />
                            </div>
                        </div>

                        {/* Listas de Pontos (EXPANDIDA) */}
                        <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                {/* Pontos Positivos */}
                                <div>
                                    <h3 className="text-sm font-bold text-green-700 uppercase tracking-wider mb-3 flex items-center gap-2 sticky top-[72px] bg-white/95 backdrop-blur py-2 z-20">
                                        <CheckCircle size={16} /> Positivos
                                    </h3>
                                    <div className="grid grid-cols-1 gap-2">
                                        {positivePoints.map(point => (
                                            <PointCard
                                                key={point.id}
                                                point={point}
                                                isSelected={selectedPointIds.includes(point.id)}
                                                onClick={togglePoint}
                                            />
                                        ))}
                                        {positivePoints.length === 0 && <p className="text-xs text-gray-400 italic">Nenhum ponto encontrado.</p>}
                                    </div>
                                </div>

                                {/* Pontos a Desenvolver */}
                                <div>
                                    <h3 className="text-sm font-bold text-yellow-700 uppercase tracking-wider mb-3 flex items-center gap-2 sticky top-[72px] bg-white/95 backdrop-blur py-2 z-20">
                                        <AlertCircle size={16} /> A Desenvolver
                                    </h3>
                                    <div className="grid grid-cols-1 gap-2">
                                        {developPoints.map(point => (
                                            <PointCard
                                                key={point.id}
                                                point={point}
                                                isSelected={selectedPointIds.includes(point.id)}
                                                onClick={togglePoint}
                                            />
                                        ))}
                                        {developPoints.length === 0 && <p className="text-xs text-gray-400 italic">Nenhum ponto encontrado.</p>}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* FOOTER - PAINEL DE AÇÃO (STICKY BOTTOM) */}
                        <div className="bg-stone-900 text-white p-4 sticky bottom-0 z-30 rounded-b-xl shadow-lg border-t border-stone-800">
                            <div className="flex justify-between items-center text-sm mb-3">
                                <div className="flex gap-4">
                                    <div className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                        <span className="text-xs opacity-80">Positivos: {selectedPointIds.filter(id => id.startsWith('P')).length}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                        <span className="text-xs opacity-80">A Desenvolver: {selectedPointIds.filter(id => id.startsWith('N')).length}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={generateReport}
                                disabled={isLoading}
                                className={`w-full py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors ${isLoading ? 'bg-stone-700 cursor-wait' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                                    }`}
                            >
                                {isLoading ? (
                                    <>
                                        <RefreshCw className="animate-spin" size={18} /> Gerando...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={18} /> Gerar Relatório
                                    </>
                                )}
                            </button>
                            {error && <p className="text-red-300 text-xs mt-2 text-center">{error}</p>}
                        </div>

                    </div>
                </div>

                {/* COLUNA DA DIREITA: RESULTADO (STICKY) */}
                <div className="lg:col-span-3 flex flex-col lg:sticky lg:top-4 self-start">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex flex-col p-5">
                        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <FileText size={16} className="text-blue-500" /> Resultado Gerado
                        </h2>

                        <div className="min-h-[200px] bg-gray-50 rounded-lg border border-gray-200 p-4 relative">
                            {generatedReport ? (
                                <textarea
                                    value={generatedReport}
                                    onChange={(e) => setGeneratedReport(e.target.value)}
                                    className="w-full h-96 p-2 bg-transparent text-stone-700 leading-relaxed text-justify resize-none outline-none focus:ring-0 border-none font-sans text-sm"
                                />
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-gray-400 text-center p-6">
                                    {isLoading ? (
                                        <div className="animate-pulse flex flex-col items-center">
                                            <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-3 w-24 bg-gray-200 rounded"></div>
                                            <p className="text-xs mt-4 text-blue-400">Consultando coordenação pedagógica...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <Sparkles size={48} className="mb-4 text-gray-200" />
                                            <p className="text-sm">Selecione a turma, o aluno e os pontos para gerar o relatório.</p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {generatedReport && (
                            <div className="mt-4 flex gap-2">
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(generatedReport);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-white border border-gray-300 rounded-lg text-sm font-medium text-stone-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
                                >
                                    <Copy size={16} /> Copiar
                                </button>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
